// src/pages/QuanLyNguoiDung/QuanLyNguoiDung.js
import React, { useState, useEffect, useCallback } from "react";
import { useAuthContext } from "../../context/AuthContext";
import { getAllUsers, updateUser, createUser, getAllLopHoc, getAllNganh } from "../../services/nguoiDungService";
import { useToast } from "../../components/Toast/ToastContext";
import useUnsavedChanges from "../../hooks/useUnsavedChanges";
import UnsavedModal from "../../components/UnsavedModal/UnsavedModal";
import { ROLES } from "../../constants";
import { auth, db } from "../../config/firebase";
import { doc, deleteDoc } from "firebase/firestore";
import { sanitizeInput, validatePhone, validateTen } from "../../utils/validation";
import BottomSheet from "../../components/BottomSheet/BottomSheet";
import QuanLyNguoiDungMobile from "./QuanLyNguoiDungMobile";
import QuanLyNguoiDungDesktop from "./QuanLyNguoiDungDesktop";
import QuanLyNguoiDungForm from "./QuanLyNguoiDungForm";

const DEFAULT_PASSWORD = "GiaoLy@2026";
const ROLE_LABELS = { 'ADMIN': 'Admin', 'TRUONG_NGANH': 'Trưởng ngành', 'GVCN': 'GVCN', 'GV_PHU': 'GV Phụ' };

const boDau = (str) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D');
const cleanEmail = (str) => boDau(str).replace(/\s+/g, '.').replace(/[^a-zA-Z0-9.]/g, '').replace(/\.+/g, '.').replace(/^\.|\.$/g, '').toLowerCase();
const generateEmail = (hoTen, tenThanh) => {
  if (!hoTen?.trim()) return '';
  const tenThanhPart = tenThanh?.trim() ? cleanEmail(tenThanh) + '.' : '';
  return `${tenThanhPart}${cleanEmail(hoTen)}`;
};

const QuanLyNguoiDung = () => {
  const { user } = useAuthContext();
  const { success, error } = useToast();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterNganh, setFilterNganh] = useState("");
  const [lastUpdate, setLastUpdate] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lopHocList, setLopHocList] = useState([]);
  const [nganhList, setNganhList] = useState([]);
  const [formStep, setFormStep] = useState(1);
  const totalSteps = 3;
  const [hasChanges, setHasChanges] = useState(false);
  const { showConfirm, confirmLeave, confirmStay } = useUnsavedChanges(hasChanges);
  const [confirmDialog, setConfirmDialog] = useState({ show: false, message: '', onConfirm: null, danger: false });
  const [emailAvailable, setEmailAvailable] = useState(null);
  const [emailError, setEmailError] = useState(null);
  const [emailTouched, setEmailTouched] = useState(false);
  const [form, setForm] = useState({
    hoTen: "", tenThanh: "", email: "", soDienThoai: "",
    vaiTro: [], maLopCN: "", maNganh: "", trangThai: "dangHoatDong"
  });

  // === PHÂN QUYỀN ===
  const isAdmin = user?.vaiTro?.includes(ROLES.ADMIN);
  const isTruongNganh = user?.vaiTro?.includes(ROLES.TRUONG_NGANH) && !isAdmin;
  const isGVCN = user?.vaiTro?.includes(ROLES.GVCN) && !isAdmin && !isTruongNganh;
  const canEdit = isAdmin;
  const canDelete = isAdmin;

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const [userData, lopData, nganhData] = await Promise.all([getAllUsers(), getAllLopHoc(), getAllNganh()]);
      
      let filteredData = userData;
      
      if (isTruongNganh) {
        const userNganh = user?.maNganh;
        filteredData = userData.filter(u => u.maNganh === userNganh);
      } else if (isGVCN) {
        const userLop = user?.maLopCN;
        filteredData = userData.filter(u => u.maLopCN === userLop);
      }
      
      setUsers(filteredData);
      setLopHocList(lopData);
      setNganhList(nganhData);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [isAdmin, isTruongNganh, isGVCN, user?.maNganh, user?.maLopCN]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  useEffect(() => {
    let result = users;
    if (searchTerm.trim()) {
      const t = searchTerm.toLowerCase().trim();
      result = result.filter(u => (u.hoTen || "").toLowerCase().includes(t) || (u.tenThanh || "").toLowerCase().includes(t) || (u.email || "").toLowerCase().includes(t));
    }
    if (filterRole) result = result.filter(u => (u.vaiTro || []).includes(filterRole));
    if (filterNganh) result = result.filter(u => u.maNganh === filterNganh);
    result = [...result].sort((a, b) => {
      const dateA = a.ngayCapNhat || a.ngayTao || '';
      const dateB = b.ngayCapNhat || b.ngayTao || '';
      return dateB.localeCompare(dateA);
    });
    setFilteredUsers(result);
  }, [users, searchTerm, filterRole, filterNganh, lastUpdate]);

  const filteredLopHoc = form.maNganh ? lopHocList.filter(l => l.nganhId === form.maNganh) : lopHocList;

  const checkEmailExists = (input) => {
    if (!input?.trim()) { setEmailAvailable(null); return; }
    const fullEmail = `${cleanEmail(input)}@xudoan.com`;
    const exists = users.some(u => u.email?.toLowerCase() === fullEmail.toLowerCase());
    setEmailAvailable(!exists);
  };

  const resetForm = () => {
    setEditingUser(null); setFormStep(1); setEmailAvailable(null); setEmailError(null); setEmailTouched(false);
    setForm({ hoTen: "", tenThanh: "", email: "", soDienThoai: "", vaiTro: [], maLopCN: "", maNganh: "", trangThai: "dangHoatDong" });
    setHasChanges(false);
  };

  const openAddForm = () => { resetForm(); setShowForm(true); };
  
  const openEditForm = (u) => {
    setEditingUser(u); setFormStep(1); setEmailAvailable(null); setEmailError(null); setEmailTouched(false);
    setForm({
      hoTen: u.hoTen || "", tenThanh: u.tenThanh || "", email: (u.email || "").replace('@xudoan.com', ''),
      soDienThoai: u.soDienThoai || "", vaiTro: u.vaiTro || [],
      maLopCN: u.maLopCN || "", maNganh: u.maNganh || "", trangThai: u.trangThai || "dangHoatDong"
    });
    setHasChanges(false); setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setHasChanges(false); setFormStep(1); setEmailAvailable(null); setEmailError(null); };

  const updateForm = (field, value) => {
    setHasChanges(true);
    setForm(prev => {
      const newForm = { ...prev, [field]: value };
      if (field === 'email') {
        setEmailTouched(true);
        if (value.trim() && /[^a-zA-Z0-9.]/.test(value)) {
          setEmailError('Không được dùng dấu, khoảng trắng hoặc ký tự đặc biệt!'); setEmailAvailable(null);
        } else if (value.trim() && /\s/.test(value)) {
          setEmailError('Không được có khoảng trắng!'); setEmailAvailable(null);
        } else if (value.trim()) {
          setEmailError(null); checkEmailExists(value);
        } else {
          setEmailError(null); setEmailAvailable(null);
        }
      }
      if (!editingUser && !emailTouched && (field === 'hoTen' || field === 'tenThanh')) {
        const suggested = generateEmail(field === 'hoTen' ? value : prev.hoTen, field === 'tenThanh' ? value : prev.tenThanh);
        if (suggested) { newForm.email = suggested; checkEmailExists(suggested); }
      }
      return newForm;
    });
  };

  const handleSave = async () => {
    if (!validateTen(form.hoTen)) { error("Họ tên không được để trống!"); return; }
    if (!form.email.trim()) { error("Tên đăng nhập không được để trống!"); return; }
    if (emailError) { error(emailError); return; }
    const emailClean = cleanEmail(form.email.trim());
    if (!emailClean) { error("Tên đăng nhập không hợp lệ!"); return; }
    const fullEmail = `${emailClean}@xudoan.com`;
    if (!editingUser && emailAvailable === false) { error("Tên đăng nhập đã tồn tại!"); return; }
    if (form.soDienThoai && !validatePhone(form.soDienThoai)) { error("SĐT không hợp lệ!"); return; }
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const cleanData = {
        hoTen: sanitizeInput(form.hoTen), tenThanh: sanitizeInput(form.tenThanh),
        email: fullEmail, soDienThoai: sanitizeInput(form.soDienThoai),
        vaiTro: form.vaiTro, maLopCN: sanitizeInput(form.maLopCN),
        maNganh: sanitizeInput(form.maNganh), trangThai: form.trangThai,
      };
      if (editingUser) {
        try { await updateUser(editingUser.uid, { ...cleanData, ngayCapNhat: now }); success("Cập nhật thành công!"); closeForm(); setLastUpdate(Date.now()); loadUsers(); }
        catch (updateErr) { error("Lỗi: " + (updateErr.message || "Không thể cập nhật")); }
        finally { setSaving(false); }
        return;
      }
      try {
        const apiKey = auth.app.options.apiKey;
        const response = await fetch(`https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${apiKey}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: fullEmail, password: DEFAULT_PASSWORD, returnSecureToken: false })
        });
        const data = await response.json();
        if (!response.ok) {
          if (data.error?.message === 'EMAIL_EXISTS') { error("Email đã tồn tại!"); setSaving(false); return; }
          throw new Error(data.error?.message || 'Không thể tạo user');
        }
        await createUser(data.localId, { ...cleanData, lanDauDangNhap: true, ngayTao: now, ngayCapNhat: now });
        success(`Đã thêm ${form.hoTen}!`); closeForm(); setLastUpdate(Date.now()); loadUsers();
      } catch (authErr) { error("Lỗi: " + (authErr.message || "Không thể tạo user")); }
      finally { setSaving(false); }
    } catch (err) { error("Lỗi: " + (err.message || "Không thể lưu")); setSaving(false); }
  };

  const handleSaveAndLeave = async () => { await handleSave(); if (!saving) confirmLeave(); };

  const handleToggleStatus = (u) => {
    const newStatus = u.trangThai === 'nghi' ? 'dangHoatDong' : 'nghi';
    setConfirmDialog({ show: true, danger: newStatus === 'nghi',
      message: `${newStatus === 'nghi' ? 'Vô hiệu hóa' : 'Kích hoạt lại'} "${u.hoTen}"?`,
      onConfirm: async () => {
        try { await updateUser(u.uid, { trangThai: newStatus, ngayCapNhat: new Date().toISOString() }); success("Thành công!"); setLastUpdate(Date.now()); loadUsers(); }
        catch (err) { error("Lỗi: " + err.message); }
        setConfirmDialog({ show: false, message: '', onConfirm: null, danger: false });
      }
    });
  };

  const handleDelete = (u) => {
    setConfirmDialog({ show: true, danger: true,
      message: `XÓA VĨNH VIỄN "${u.hoTen}"?\n\n⚠️ Không thể hoàn tác!`,
      onConfirm: async () => {
        try { await deleteDoc(doc(db, "nguoiDung", u.uid)); success("Đã xóa!"); loadUsers(); }
        catch (err) { error("Lỗi: " + err.message); }
        setConfirmDialog({ show: false, message: '', onConfirm: null, danger: false });
      }
    });
  };

  const handleResetPassword = (u) => {
    setConfirmDialog({ show: true, danger: false,
      message: `Yêu cầu "${u.hoTen}" đổi mật khẩu khi đăng nhập lần sau?`,
      onConfirm: async () => {
        try { await updateUser(u.uid, { lanDauDangNhap: true, ngayCapNhat: new Date().toISOString() }); success("Đã yêu cầu đổi MK!"); setLastUpdate(Date.now()); loadUsers(); }
        catch (err) { error("Lỗi: " + err.message); }
        setConfirmDialog({ show: false, message: '', onConfirm: null, danger: false });
      }
    });
  };

  const getTenNganh = (ma) => nganhList.find(n => n.maNganh === ma)?.tenNganh || ma;
  const getTenLop = (ma) => lopHocList.find(l => l.maLop === ma)?.tenLop || ma;

  if (!isAdmin && !isTruongNganh && !isGVCN) {
    return <div className="flex items-center justify-center h-64"><div className="text-center"><span className="material-symbols-outlined text-[48px]">lock</span><p>⛔ Không có quyền</p></div></div>;
  }

  return (
    <div className="space-y-4 pb-20 md:pb-6">
      <UnsavedModal show={showConfirm} onStay={confirmStay} onLeave={confirmLeave} onSaveAndLeave={handleSaveAndLeave} />
      
      {confirmDialog.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={() => setConfirmDialog({ show: false, message: '', onConfirm: null, danger: false })}>
          <div className="bg-surface rounded-xl p-6 shadow-lg max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4"><span className={`material-symbols-outlined text-[24px] ${confirmDialog.danger ? 'text-error' : 'text-yellow-600'}`}>{confirmDialog.danger ? 'warning' : 'help'}</span><p className="text-body-md whitespace-pre-line">{confirmDialog.message}</p></div>
            <div className="flex gap-2 justify-end"><button onClick={() => setConfirmDialog({ show: false, message: '', onConfirm: null, danger: false })} className="px-4 py-2 bg-surface border border-outline-variant rounded-xl text-body-md font-semibold">Hủy</button><button onClick={confirmDialog.onConfirm} className={`px-4 py-2 rounded-xl text-body-md font-semibold text-white ${confirmDialog.danger ? 'bg-error' : 'bg-primary'}`}>{confirmDialog.danger ? 'Xóa' : 'Xác nhận'}</button></div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div><h2 className="text-[24px] md:text-[28px] font-bold text-primary">👤 Quản lý người dùng</h2><p className="text-caption text-on-surface-variant">{users.length} người dùng</p></div>
        {isAdmin && (
          <button onClick={openAddForm} className="hidden md:flex px-4 py-2.5 bg-primary text-on-primary rounded-full text-body-md font-semibold items-center gap-1.5"><span className="material-symbols-outlined text-[18px]">add</span>Thêm mới</button>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-outline">search</span>
          <input type="text" placeholder="Tìm kiếm..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full h-12 pl-11 pr-4 bg-surface-container-lowest border border-outline-variant rounded-xl focus:border-primary focus:ring-1 focus:ring-primary outline-none text-body-md" />
          {searchTerm && <button onClick={() => setSearchTerm("")} className="absolute right-3 top-1/2 -translate-y-1/2"><span className="material-symbols-outlined text-[18px]">close</span></button>}
        </div>
        <select value={filterRole} onChange={e => setFilterRole(e.target.value)} className="hidden md:block bg-surface border border-outline-variant rounded-xl px-3 py-2.5 text-body-md outline-none"><option value="">Vai trò</option>{Object.entries(ROLES).map(([k, v]) => <option key={k} value={v}>{ROLE_LABELS[v]}</option>)}</select>
        <select value={filterNganh} onChange={e => setFilterNganh(e.target.value)} className="hidden md:block bg-surface border border-outline-variant rounded-xl px-3 py-2.5 text-body-md outline-none"><option value="">Ngành</option>{nganhList.map(n => <option key={n.id} value={n.maNganh}>{n.tenNganh}</option>)}</select>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1 md:hidden">
        {[{ value: '', label: 'Tất cả' }, { value: 'GVCN', label: 'GVCN' }, { value: 'TRUONG_NGANH', label: 'Tr.Ngành' }, { value: 'GV_PHU', label: 'Phụ tá' }, { value: 'ADMIN', label: 'Admin' }].map(c => <button key={c.value} onClick={() => setFilterRole(c.value)} className={`px-4 py-1.5 rounded-full text-caption font-bold flex-shrink-0 ${filterRole === c.value ? 'bg-secondary-fixed text-on-secondary-fixed-variant' : 'bg-surface-container text-on-surface-variant'}`}>{c.label}</button>)}
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1 md:hidden">
        {[{ value: '', label: 'Tất cả ngành' }, ...nganhList.map(n => ({ value: n.maNganh, label: n.tenNganh }))].map(c => <button key={c.value} onClick={() => setFilterNganh(c.value)} className={`px-4 py-1.5 rounded-full text-caption font-bold flex-shrink-0 ${filterNganh === c.value ? 'bg-secondary-fixed text-on-secondary-fixed-variant' : 'bg-surface-container text-on-surface-variant'}`}>{c.label}</button>)}
      </div>

      {loading ? <div className="flex items-center justify-center h-64"><span className="material-symbols-outlined text-primary animate-spin text-4xl">sync</span></div> : (
        <>
          <div className="md:hidden"><QuanLyNguoiDungMobile filteredUsers={filteredUsers} getTenNganh={getTenNganh} getTenLop={getTenLop} onEdit={openEditForm} onResetPassword={handleResetPassword} onToggleStatus={handleToggleStatus} onDelete={handleDelete} isAdmin={isAdmin} /></div>
          <div className="hidden md:block"><QuanLyNguoiDungDesktop filteredUsers={filteredUsers} getTenNganh={getTenNganh} getTenLop={getTenLop} onEdit={openEditForm} onResetPassword={handleResetPassword} onToggleStatus={handleToggleStatus} onDelete={handleDelete} isAdmin={isAdmin} /></div>
        </>
      )}

      {isAdmin && <button onClick={openAddForm} className="md:hidden fixed bottom-24 right-6 w-14 h-14 bg-primary text-on-primary rounded-2xl shadow-lg flex items-center justify-center active:scale-95 transition-transform z-50"><span className="material-symbols-outlined text-[28px]">add</span></button>}

      <BottomSheet isOpen={showForm} onClose={() => hasChanges ? setConfirmDialog({ show: true, message: 'Có thay đổi chưa lưu. Đóng?', danger: false, onConfirm: () => { closeForm(); setConfirmDialog({ show: false, message: '', onConfirm: null, danger: false }); } }) : closeForm()} title={editingUser ? "✏️ Sửa" : "➕ Thêm mới"}>
        <QuanLyNguoiDungForm form={form} editingUser={editingUser} formStep={formStep} totalSteps={totalSteps} nganhList={nganhList} filteredLopHoc={filteredLopHoc} saving={saving} emailAvailable={emailAvailable} emailError={emailError} DEFAULT_PASSWORD={DEFAULT_PASSWORD} generateEmail={generateEmail} updateForm={updateForm} setForm={setForm} setHasChanges={setHasChanges} setFormStep={setFormStep} onSave={handleSave} onClose={closeForm} />
      </BottomSheet>
    </div>
  );
};

export default QuanLyNguoiDung;